import { createRoute, redirect } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { IconCheck } from '@tabler/icons-react';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import { ApiError } from '../../../../lib/api.js';
import { useBranding, useUpdateBranding } from '../../../../hooks/useBranding.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/Input.js';
import { Label } from '../../../../components/ui/Label.js';
import { Textarea } from '../../../../components/ui/Textarea.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/branding',
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (role !== 'admin') throw redirect({ to: '/app' });
  },
  component: BrandingPage,
});

const DEFAULT_COLOR = '#0D2B4A';
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function BrandingPage() {
  const branding = useBranding();
  const update = useUpdateBranding();

  const [color, setColor] = useState(DEFAULT_COLOR);
  const [signature, setSignature] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed the form once the current branding loads.
  useEffect(() => {
    if (branding.data) {
      setColor(branding.data.primary_color ?? DEFAULT_COLOR);
      setSignature(branding.data.signature_html ?? '');
    }
  }, [branding.data]);

  const colorValid = HEX_RE.test(color);
  // Never let an invalid hex break the live preview — fall back to navy.
  const previewColor = colorValid ? color : DEFAULT_COLOR;

  async function handleSave() {
    setError(null);
    setSaved(false);
    if (!colorValid) {
      setError('Enter a 6-digit hex colour, e.g. #0D2B4A.');
      return;
    }
    try {
      await update.mutateAsync({ primary_color: color, signature_html: signature });
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    }
  }

  return (
    <div>
      <AdminPageHeader
        title="Branding"
        description="Set the colour and signature that appear on the overviews and checklists your team sends."
      />

      {branding.isLoading && <p className="text-muted">Loading…</p>}
      {branding.error && (
        <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3">
          Couldn't load branding: {(branding.error as Error).message}
        </p>
      )}

      {branding.data && (
        <div className="max-w-2xl space-y-8">
          {saved && (
            <div
              className="rounded-lg px-4 py-3 text-sm flex items-center gap-2"
              style={{ backgroundColor: '#E8F3EC', color: '#1E7A47', border: '1px solid #C9E5D2' }}
            >
              <IconCheck size={16} /> <strong>Saved.</strong> New sends will use this branding.
            </div>
          )}
          {error && (
            <div
              className="rounded-md px-3 py-2 text-[13px]"
              style={{ backgroundColor: '#FBEAEA', color: '#A83232', border: '1px solid #F0D0D0' }}
            >
              {error}
            </div>
          )}

          {/* Primary colour */}
          <section>
            <Label htmlFor="brand-color">Primary colour</Label>
            <p className="text-xs text-muted mb-2">
              Used for headings and accents on the PDF and email. Defaults to navy when unset.
            </p>
            <div className="flex items-center gap-3">
              <input
                id="brand-color"
                type="color"
                value={colorValid ? color : DEFAULT_COLOR}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 w-14 rounded-lg border border-line cursor-pointer bg-white p-1"
                aria-label="Pick primary colour"
              />
              <Input
                value={color}
                onChange={(e) => setColor(e.target.value)}
                invalid={!colorValid}
                placeholder="#0D2B4A"
                className="max-w-[160px] font-mono"
              />
            </div>
          </section>

          {/* Signature */}
          <section>
            <Label htmlFor="brand-signature">Email &amp; PDF signature</Label>
            <p className="text-xs text-muted mb-2">
              Basic HTML is allowed (links, bold, line breaks). Scripts and images are removed for
              safety. Leave blank to fall back to the consultant&rsquo;s name.
            </p>
            <Textarea
              id="brand-signature"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              rows={6}
              placeholder={'<strong>Jane Doe</strong><br>Senior Consultant, Your Agency'}
              className="font-mono text-[13px]"
            />
          </section>

          {/* Live preview — mirrors the overview email/PDF, recoloured from the form above. */}
          <section>
            <Label>Preview</Label>
            <p className="text-xs text-muted mb-2">
              How your colour and signature appear on the overviews, checklists and quotes your team
              sends.
            </p>
            <div className="rounded-xl border border-line bg-white p-6 shadow-sm">
              <div
                className="h-[3px] w-10 rounded mb-4"
                style={{ backgroundColor: previewColor }}
                aria-hidden
              />
              <h3
                className="font-serif text-xl font-medium leading-tight tracking-tight"
                style={{ color: previewColor }}
              >
                Master of Information Technology
              </h3>
              <div className="text-xs text-muted mt-1">Provider &middot; CRICOS 012345</div>
              <div className="mt-4 border-t border-line pt-4 text-sm text-ink">
                {signature.trim().length > 0 ? (
                  // Preview only — the server sanitises before storing/sending.
                  <div dangerouslySetInnerHTML={{ __html: signature }} />
                ) : (
                  <div className="text-muted">
                    <strong>Your name</strong>
                    <br />
                    Your agency
                  </div>
                )}
              </div>
            </div>
          </section>

          <div className="flex items-center gap-3">
            <Button
              onClick={handleSave}
              loading={update.isPending}
              disabled={!colorValid}
            >
              Save branding
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
